/**
 * SuggestFixModal.jsx — "Suggest a Fix" workflow for member profiles.
 *
 * Any signed-in user can propose a structured correction to a field
 * on a member profile. The suggestion is stored in
 * `profile_corrections` and the tree owner / admins / editors review
 * and apply it from the admin queue.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const FIELDS = [
  { v: "first_name",     label: "First name" },
  { v: "middle_name",    label: "Middle name" },
  { v: "last_name",      label: "Last name" },
  { v: "nickname",       label: "Nickname" },
  { v: "maiden_name",    label: "Maiden name" },
  { v: "birth_date",     label: "Birth date" },
  { v: "birth_place",    label: "Birth place" },
  { v: "death_date",     label: "Death date" },
  { v: "death_place",    label: "Death place" },
  { v: "current_status", label: "Living / Deceased status" },
  { v: "gender",         label: "Gender" },
  { v: "bio",            label: "Biography" },
  { v: "occupation",     label: "Occupation / Career" },
  { v: "education",      label: "Education" },
  { v: "religion",       label: "Religion" },
  { v: "languages",      label: "Languages" },
  { v: "residences",     label: "Residence history" },
  { v: "military",       label: "Military service" },
  { v: "awards",         label: "Awards" },
  { v: "other",          label: "Other (please describe)" },
];

export default function SuggestFixModal({ personId, memberName, currentValues = {}, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [field, setField] = useState("bio");
  const [suggested, setSuggested] = useState("");
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} className="rounded-2xl max-w-sm p-6 shadow-xl" style={{ background: "var(--wofy-paper)" }}>
          <h2 className="font-display text-xl mb-2">Sign in to suggest an edit</h2>
          <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>Corrections are attributed to your account so family managers can follow up if they need more context.</p>
          <button onClick={onClose} className="wofy-btn-ghost text-sm">Close</button>
        </div>
      </div>
    );
  }

  const currentValue = currentValues[field];

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!suggested.trim()) return toast.error("Please enter the suggested value.");
    setBusy(true);
    try {
      await http.post(`/persons/${personId}/corrections`, {
        field,
        original_value: currentValue ? String(currentValue) : null,
        suggested_value: suggested.trim(),
        reason: reason.trim() || null,
        source_url: source.trim() || null,
      });
      toast.success("Thanks — your suggestion is queued for family review.");
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="suggest-fix-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl w-full max-w-md my-auto p-6 space-y-4 shadow-xl"
        style={{ background: "var(--wofy-paper)" }}
      >
        <header className="flex items-start justify-between">
          <div>
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>SUGGEST A FIX</div>
            <h2 className="font-display text-xl">Propose an edit to {memberName || "this profile"}</h2>
          </div>
          <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="suggest-fix-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          Family managers review every suggestion. Canonical profile data is only updated after they approve — nothing is auto-applied.
        </p>

        <label className="block">
          <div className="text-[11px] font-mono uppercase tracking-wider mb-1 opacity-70">Field</div>
          <select
            value={field}
            onChange={(e) => setField(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="suggest-fix-field"
          >
            {FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
          </select>
        </label>

        {currentValue != null && String(currentValue).length > 0 && (
          <div className="text-xs rounded-lg p-2" style={{ background: "var(--wofy-surface)", color: "var(--wofy-text-soft)" }} data-testid="suggest-fix-current">
            <b>Current:</b> <span className="font-mono">{String(currentValue).slice(0, 200)}</span>
          </div>
        )}

        <label className="block">
          <div className="text-[11px] font-mono uppercase tracking-wider mb-1 opacity-70">Suggested value</div>
          <textarea
            value={suggested}
            onChange={(e) => setSuggested(e.target.value)}
            placeholder="Enter the correct value…"
            rows={3}
            maxLength={4000}
            required
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="suggest-fix-value"
          />
        </label>

        <label className="block">
          <div className="text-[11px] font-mono uppercase tracking-wider mb-1 opacity-70">Why this change? (optional)</div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="I'm his daughter · saw in the baptism register · etc."
            maxLength={1000}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="suggest-fix-reason"
          />
        </label>

        <label className="block">
          <div className="text-[11px] font-mono uppercase tracking-wider mb-1 opacity-70">Source URL (optional)</div>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="Link to news article / obituary / register scan"
            maxLength={1000}
            className="w-full rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: "var(--wofy-line)" }}
            data-testid="suggest-fix-source"
          />
        </label>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="wofy-btn-ghost text-sm" data-testid="suggest-fix-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="wofy-btn-primary text-sm" data-testid="suggest-fix-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
            {busy ? "Sending…" : "Submit for review"}
          </button>
        </div>
      </form>
    </div>
  );
}
